import { forwardRef, Fragment } from 'react';
import { faHouse } from '@fortawesome/pro-light-svg-icons';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import { VariantProps, tv } from 'tailwind-variants';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from './icon.component';

import { BreadCrumbItems } from '@/types/breadcrumb-items.type';
import {
  BreadcrumbBase,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb-base.component';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const breadcrumbContainer = tv({
  slots: {
    container: ['h-10 flex justify-start items-center bg-gray-200/40 border-spacing-2'],
    listMobile: ['px-5 md:px-10 flex-nowrap h-full w-[95%] flex md:hidden'],
    listDesktop: ['px-5 md:px-10 flex-nowrap h-full w-[80%] hidden md:flex'],
    button: ['h-3 p-1 bg-gray-300 flex items-center justify-between rounded'],
    dropdownContent: [
      'mt-1.5 translate-x-12 data-[state=open]:animate-breadcrumb-down data-[state=closed]:animate-breadcrumb-up rounded',
    ],
    dropdownItem: ['p-3'],
    link: ['text-green-500 text-base hover:text-green-700 hover:underline'],
    page: ['text-gray-500 font-normal text-base'],
    truncated: [
      'truncate block max-w-[140px] xsm:max-w-[200px] sm:max-w-full overflow-hidden text-ellipsis whitespace-nowrap',
    ],
  },
});

export interface BreadcrumbProps extends VariantProps<typeof breadcrumbContainer> {
  className?: string;
  abbreviated?: boolean;
  list: BreadCrumbItems;
}

export const Breadcrumb = forwardRef<HTMLDivElement, BreadcrumbProps>(
  ({ className, abbreviated = false, list }, ref) => {
    const { container, listMobile, listDesktop, button, dropdownContent, dropdownItem, link, page, truncated }
      = breadcrumbContainer();
    const size = list.length;

    return (
      <BreadcrumbBase ref={ref} className={container({ className })} data-testid={DataTestIdEnum.BREADCRUMB_CONTAINER}>
        <BreadcrumbList className={listMobile()}>
          <BreadcrumbItem>
            <BreadcrumbLink
              href="/"
              aria-label="Página inicial"
              data-testid={DataTestIdEnum.BREADCRUMB_HOUSE_ICON_LINK}
            >
              <Icon icon={faHouse} size="lg" className="text-green-500" />
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />

          {size > 1 && abbreviated
            ? (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button aria-label="Abrir breadcrumb" className={button()}>
                        <Icon icon={faEllipsis} className="text-xl" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className={dropdownContent()}>
                      {list.slice(0, size - 1).map((item) => (
                        <DropdownMenuItem key={item.href ?? item.label} className={dropdownItem()}>
                          <BreadcrumbLink href={item.href} className={`${link()} ${truncated()}`} title={item.label}>
                            {item.label}
                          </BreadcrumbLink>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <BreadcrumbSeparator />
                  <BreadcrumbItem className="truncate">
                    <BreadcrumbPage className={`${page()} ${truncated()}`}>{list[size - 1].label}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )
            : (
                list.map((item, index) => {
                  const isLast = index === size - 1;
                  return (
                    <Fragment key={item.href ?? item.label}>
                      {index > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem className="truncate">
                        {!isLast
                          ? (
                              <BreadcrumbLink
                                href={item.href}
                                className={`${link()} ${truncated()}`}
                                title={item.label}
                                data-testid={DataTestIdEnum.BREADCRUMB_OPTION}
                              >
                                {item.label}
                              </BreadcrumbLink>
                            )
                          : (
                              <BreadcrumbPage
                                className={`${page()} ${truncated()}`}
                                title={item.label}
                                data-testid={DataTestIdEnum.BREADCRUMB_OPTION}
                              >
                                {item.label}
                              </BreadcrumbPage>
                            )}
                      </BreadcrumbItem>
                    </Fragment>
                  );
                })
              )}
        </BreadcrumbList>

        <BreadcrumbList className={listDesktop()}>
          <BreadcrumbItem>
            <BreadcrumbLink
              href="/"
              aria-label="Página inicial"
              data-testid={DataTestIdEnum.BREADCRUMB_HOUSE_ICON_LINK}
            >
              <Icon icon={faHouse} size="lg" className="text-green-500" />
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />

          {list.map((item, index) => {
            const isLast = index === size - 1;
            return (
              <Fragment key={item.href ?? item.label}>
                {!isLast
                  ? (
                      <>
                        <BreadcrumbItem className="truncate">
                          <BreadcrumbLink
                            href={item.href}
                            className={`${link()} ${truncated()}`}
                            title={item.label}
                            data-testid={DataTestIdEnum.BREADCRUMB_OPTION}
                          >
                            {item.label}
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                      </>
                    )
                  : (
                      <BreadcrumbItem className="truncate">
                        <BreadcrumbPage
                          className={`${page()} ${truncated()}`}
                          title={item.label}
                          data-testid={DataTestIdEnum.BREADCRUMB_OPTION}
                        >
                          {item.label}
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    )}
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </BreadcrumbBase>
    );
  },
);

Breadcrumb.displayName = 'Breadcrumb';
